import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
  Box,
  Avatar,
  Typography,
  Fade,
  CircularProgress,
  useMediaQuery,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {
  getFirestore,
  collection,
  addDoc,
  Timestamp,
  doc,
  setDoc,
} from "firebase/firestore";
import { auth } from "../../firebase";
import { encryptField } from "../../utils/encryption";

function getInitials(name) {
  if (!name) return "";
  const names = name.trim().split(" ");
  if (names.length === 1) return names[0][0]?.toUpperCase() || "";
  return (names[0][0] + (names[1]?.[0] || "")).toUpperCase();
}

export default function ClientForm({ open, onClose, onSuccess, initialData }) {
  const db = getFirestore();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    address: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const nameRef = useRef(null);
  const emailRef = useRef(null);

  const handleChange = (e) => {
    const updatedForm = { ...form, [e.target.name]: e.target.value };
    setForm(updatedForm);
    validate(updatedForm);
  };

  const validate = (data = form) => {
    const newErrors = {};
    if (!data.name.trim()) newErrors.name = "Name is required";
    if (!data.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = "Invalid email";
    }
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors.name) nameRef.current?.focus();
      else if (validationErrors.email) emailRef.current?.focus();
      return;
    }

    setSaving(true);
    const user = auth.currentUser;
    if (!user) {
      setSaving(false);
      return alert("Not logged in");
    }

    const encryptedForm = {
      name: encryptField(form.name),
      email: encryptField(form.email),
      company: encryptField(form.company),
      address: encryptField(form.address),
      notes: encryptField(form.notes),
    };

    if (initialData?.id) {
      const clientRef = doc(db, "users", user.uid, "clients", initialData.id);
      await setDoc(clientRef, encryptedForm, { merge: true });
      setShowSuccess(true);
      onSuccess("Client updated");
    } else {
      const clientRef = collection(db, "users", user.uid, "clients");
      await addDoc(clientRef, {
        ...encryptedForm,
        createdAt: Timestamp.now(),
      });
      setShowSuccess(true);
      onSuccess("Client added");
    }
    setSaving(false);
    setTimeout(() => setShowSuccess(false), 2000);
  };

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        email: initialData.email || "",
        company: initialData.company || "",
        address: initialData.address || "",
        notes: initialData.notes || "",
      });
      validate(initialData);
    } else {
      const blank = {
        name: "",
        email: "",
        company: "",
        address: "",
        notes: "",
      };
      setForm(blank);
      validate(blank);
    }
    setErrors({});
    setShowSuccess(false);
  }, [initialData, open]);

  // UI layout
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 0,
          background: "rgba(255,255,255,0.98)",
          boxShadow: "0 6px 48px 0 #38bdf81a",
          overflow: "visible",
        },
      }}
      aria-labelledby="client-form-title"
    >
      <DialogTitle
        sx={{
          px: isMobile ? 2 : 4,
          pt: isMobile ? 2.5 : 4,
          pb: 0,
          fontWeight: 700,
          fontSize: "2rem",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
        id="client-form-title"
      >
        <Avatar
          sx={{
            bgcolor: "#bae6fd",
            color: "#0369a1",
            width: 48,
            height: 48,
            fontSize: 28,
            fontWeight: 700,
            mr: 1,
          }}
        >
          {form.name.trim() ? (
            getInitials(form.name)
          ) : (
            <PeopleAltRoundedIcon sx={{ fontSize: 32 }} />
          )}
        </Avatar>
        {initialData ? "Edit Client" : "Add Client"}
      </DialogTitle>
      <DialogContent
        sx={{
          px: isMobile ? 2 : 4,
          pt: 2,
          pb: 0,
          background: "linear-gradient(90deg,#f0fdfa 0%,#f3f4f6 100%)",
          borderRadius: "0 0 32px 32px",
          minHeight: isMobile ? 0 : 320,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 500,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 19, color: "#38bdf8" }} />
          Why save clients? Fast invoicing, less typing, and a clean business
          record.
        </Typography>

        <TextField
          fullWidth
          name="name"
          label="Client Name"
          inputRef={nameRef}
          autoFocus
          margin="normal"
          value={form.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name || " "}
          InputLabelProps={{
            style: { fontWeight: 600 },
          }}
        />
        <TextField
          fullWidth
          name="email"
          label="Email"
          inputRef={emailRef}
          margin="normal"
          value={form.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email || "For invoices & reminders"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="We never share client emails with anyone">
                  <IconButton size="small" tabIndex={-1}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            style: { fontWeight: 600 },
          }}
        />
        <TextField
          fullWidth
          name="company"
          label="Company (optional)"
          margin="normal"
          value={form.company}
          onChange={handleChange}
          InputLabelProps={{
            style: { fontWeight: 600 },
          }}
        />
        <TextField
          fullWidth
          name="address"
          label="Address (optional)"
          margin="normal"
          multiline
          minRows={2}
          value={form.address}
          onChange={handleChange}
          InputLabelProps={{
            style: { fontWeight: 600 },
          }}
        />
        <TextField
          fullWidth
          name="notes"
          label="Notes (e.g. preferred terms, contact details)"
          margin="normal"
          multiline
          minRows={2}
          value={form.notes}
          onChange={handleChange}
          InputLabelProps={{
            style: { fontWeight: 600 },
          }}
        />
      </DialogContent>

      {/* Sticky action bar */}
      <DialogActions
        sx={{
          px: isMobile ? 2 : 4,
          pb: isMobile ? 2 : 3,
          pt: 2,
          borderTop: "1px solid #e0e7ef",
          justifyContent: "space-between",
          position: isMobile ? "sticky" : "static",
          bottom: 0,
          background: isMobile ? "#fff" : "none",
          zIndex: 1,
        }}
      >
        <Button onClick={onClose} variant="text" sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Box position="relative" display="inline-flex">
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValid || saving}
            color="primary"
            sx={{
              borderRadius: 99,
              minWidth: 110,
              px: 3,
              fontWeight: 700,
              fontSize: 16,
              boxShadow: saving ? "0 0 0 2px #bae6fd" : "",
              transition: "box-shadow 0.2s",
            }}
          >
            {saving ? (
              <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
            ) : showSuccess ? (
              <Fade in>
                <CheckCircleRoundedIcon
                  color="success"
                  sx={{ fontSize: 22, mr: 1 }}
                />
              </Fade>
            ) : null}
            {saving
              ? "Saving..."
              : showSuccess
              ? "Saved!"
              : initialData
              ? "Update"
              : "Save"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
